// Storefront chat bubble — bootstrap loader.
//
// This file is the entry that ships in the Theme App Extension's
// <script type="module" src="…">. It must stay tiny: every storefront
// pageview pays its cost. The React chat window itself is dynamically
// imported on first click, so `widget.js` stays under ~5 KB gzipped while
// the heavy chat-mount chunk only loads when the customer engages.
//
// Mount strategy:
//   1. find `#agntc-chat-mount-target` on the page
//   2. attach a shadow DOM root for style isolation
//   3. paint a tiny DOM-only "open chat" button in the shadow root
//   4. on first click, lazy-import `./chat-mount` (the React chunk) and
//      hand off the same shadow root — the React entry tears down our
//      bootstrap button and mounts <ChatBubble defaultOpen> in its place.

import { readConfig, WidgetConfigError, type WidgetConfig } from './lib/config';

const MOUNT_TARGET_ID = 'agntc-chat-mount-target';

interface BootstrapHandle {
  shadow: ShadowRoot;
  config: WidgetConfig;
  bootstrapButton: HTMLButtonElement;
  bootstrapStyles: HTMLStyleElement;
}

function mount(): void {
  const host = document.getElementById(MOUNT_TARGET_ID);
  if (!host) {
    console.warn(`[agntc-chat] no #${MOUNT_TARGET_ID} on page; widget did not mount`);
    return;
  }

  let config: WidgetConfig;
  try {
    config = readConfig(host);
  } catch (err) {
    if (err instanceof WidgetConfigError) {
      console.warn(`[agntc-chat] ${err.message}`);
      return;
    }
    throw err;
  }

  // HMR / double-include guard.
  if (host.shadowRoot) return;
  const shadow = host.attachShadow({ mode: 'open' });

  const bootstrapStyles = document.createElement('style');
  bootstrapStyles.textContent = bootstrapCss(config);
  shadow.appendChild(bootstrapStyles);

  const bootstrapButton = document.createElement('button');
  bootstrapButton.type = 'button';
  bootstrapButton.setAttribute('aria-label', 'Open chat');
  bootstrapButton.className = 'agntc-bootstrap-bubble';
  bootstrapButton.innerHTML = chatIconSvg();
  shadow.appendChild(bootstrapButton);

  bootstrapButton.addEventListener(
    'click',
    () => void open({ shadow, config, bootstrapButton, bootstrapStyles }),
    { once: true },
  );
}

async function open(handle: BootstrapHandle): Promise<void> {
  // Dynamically import the React chunk. Vite splits this into a separate
  // `widget-chat-*.js` file; the storefront only fetches it when the
  // customer engages with the bubble.
  const mod = await import('./chat-mount');
  // Tear down the bootstrap surface so React owns the shadow root cleanly.
  handle.bootstrapButton.remove();
  handle.bootstrapStyles.remove();
  mod.mountChat({ shadow: handle.shadow, config: handle.config });
}

function bootstrapCss(config: WidgetConfig): string {
  const position = config.position === 'bottom-left' ? 'left: 1.25rem;' : 'right: 1.25rem;';
  return `
    .agntc-bootstrap-bubble {
      position: fixed;
      bottom: 1.25rem;
      ${position}
      width: 3.5rem;
      height: 3.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${config.primaryColor};
      color: #ffffff;
      border: none;
      border-radius: 9999px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2),
                  0 4px 6px -2px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      z-index: 2147483647;
      transition: transform 120ms ease;
    }
    .agntc-bootstrap-bubble:hover { transform: scale(1.05); }
    .agntc-bootstrap-bubble:active { transform: scale(0.95); }
    .agntc-bootstrap-bubble svg { width: 22px; height: 22px; }
  `;
}

function chatIconSvg(): string {
  return `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
         aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  `;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount, { once: true });
} else {
  mount();
}
